import { apiFetch } from "../lib/auth";
import type { CascadeResolveArgs, CascadeResolveResult, CascadeStatus } from "./cascade";
import type { AmbJobPublic, AmbReportResult, LoadedCounty } from "./types";

/**
 * Typed wrappers over the REAL, already-shipped `/api/amb/*`, `/api/subject`, and `/api/cotality/*`
 * endpoints (`server/amb_report.py`, `server/app.py`, `server/cotality.py`) — this file invents no
 * new backend contract. Per ADR-0003, `/next` reuses these routers UNCHANGED; this is the client
 * half of that reuse.
 */

export interface StartExtractArgs {
  files: File[];
  /** The subject's photo, when the agent dropped one in. Sent as its OWN multipart part, never
   * base64 inside `meta`: Starlette caps text FIELDS at 1 MB, which rejected any normal-sized
   * listing photo when it was embedded there (see `amb_report.py`'s note on exactly this). The
   * server validates and normalizes it to a bounded JPEG before the job is created. */
  subjectPhoto?: File | null;
  tier?: "cma_calc" | "cma_ai" | "amb";
  /** Threaded into `meta.address_line` so the extract anchors on a typed/looked-up address rather
   * than promoting the first comp to the subject (amb_report.py's documented "1820 Wood Duck" fix). */
  addressLine?: string;
  /** "Read this file on our own servers" — the agent has opted OUT of the hosted converter for
   * this upload. The document never leaves our infrastructure and no per-page conversion is
   * billed; a scanned or unreadable export goes back to the local OCR loop, which is correct and
   * slow (~13s a page measured on the production box).
   *
   * Absent or false is the DEFAULT and the fast path: the server picks the best reader per file,
   * which for anything our own readers cannot handle is the hosted converter. See
   * `IntakeScreen`'s note for the 2026-08-07 owner request that inverted this. */
  localReadOnly?: boolean;
  /** WHY THIS IS NOT `localReadOnly` RENAMED, and why the name does not lie (2026-08-07).
   *
   * These are two different decisions, not one decision under two names, so collapsing them would
   * be the actual dishonesty:
   *   * `force_mathpix` skips the "does this file need image reading at all" gate, sending a file
   *     that reads here in 200ms to the converter anyway. Its only use is a like-for-like timing
   *     A/B on a file you own, and it is no longer offered in the UI — an ordinary agent ticking
   *     it would pay per page to make a fast read slower.
   *   * `localReadOnly` skips the converter ENTIRELY, including for the files it exists for.
   * Setting both is coherent and means "no converter" — the server checks the opt-out first.
   *
   * Kept on this interface, rather than deleted, because `CmaAmbFlow.handleStart` still forwards a
   * `forceFastPdf` it no longer receives: removing the field would turn that into a TypeScript
   * excess-property error in a file this change does not touch. It now resolves to `undefined` on
   * every upload, which is exactly the new default. */
  forceFastPdf?: boolean;
}

/**
 * THE READER'S ZONE (S09, 2026-09-23). The server derives "today" — the printed retrieval date,
 * the effective date, the date the engine values on — in this zone instead of its own UTC clock,
 * which printed TOMORROW on a brief built after 6 pm in the US. An IANA name only; undefined when
 * the browser cannot say, and the server then uses its stated fallback (`server/local_date.py`).
 */
export function readerZone(): string | undefined {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof tz === "string" && tz ? tz : undefined;
  } catch {
    return undefined;
  }
}

export async function startExtract({ files, tier = "amb", addressLine, subjectPhoto, localReadOnly, forceFastPdf }: StartExtractArgs): Promise<AmbJobPublic> {
  const form = new FormData();
  form.append("tier", tier);
  // All of these travel in `meta`, which the endpoint already accepts as free-form JSON — no new
  // form field, no new backend contract. Each key is only emitted when set, so an ordinary upload
  // is byte-identical to what it sent before any of this existed — and, since the default is now
  // "let the server choose the fastest reader", the ordinary upload emits NEITHER read-path key.
  const metaObj: Record<string, unknown> = {};
  if (addressLine) metaObj.address_line = addressLine;
  if (localReadOnly) metaObj.local_read_only = true;
  if (forceFastPdf) metaObj.force_mathpix = true;
  // The zone rides with the file because the extract stamps `dom_as_of` — the day a listing's
  // days-on-market figure was true — and that day must be the agent's, not the server's (S09).
  const tz = readerZone();
  if (tz) metaObj.tz = tz;
  form.append("meta", JSON.stringify(metaObj));
  for (const f of files) form.append("files", f);
  // Optional and non-blocking: omitting it never affects the rest of intake, and when it is absent
  // the report falls back to Street View for the address.
  if (subjectPhoto) form.append("subject_photo", subjectPhoto);
  const res = await apiFetch<{ job: AmbJobPublic }>("/api/amb/extract", { method: "POST", body: form });
  return res.job;
}

export async function getJob(jobId: string): Promise<AmbJobPublic> {
  return apiFetch<AmbJobPublic>(`/api/amb/jobs/${encodeURIComponent(jobId)}`);
}

/**
 * THE CONFIRMED REVIEW, PARKED WHERE A SECOND MACHINE CAN REACH IT (2026-09-07).
 *
 * `draftStore` keeps the agent's reviewed grid in `sessionStorage` and `localStorage`, and both of
 * those are one browser on one machine. These two carry the same draft to the job row it belongs
 * to, so an agent who reviews on a laptop and opens the brief on a desktop still has a step rail
 * with doors. The server stores it opaquely and derives nothing from it — see `AmbJob.review_json`.
 */
export async function putJobReview(jobId: string, review: unknown): Promise<void> {
  await apiFetch(`/api/amb/jobs/${encodeURIComponent(jobId)}/review`,
                 { method: "PUT", body: { review } });
}

/** The stored review, or `null` when this brief has none. A job you own with no review answers
 *  200 with `review: null`; a 404 means the job is not yours or not there, and both of those are
 *  the caller's problem rather than this function's — it rethrows. */
export async function getJobReview<T>(jobId: string): Promise<T | null> {
  const res = await apiFetch<{ review: T | null }>(
    `/api/amb/jobs/${encodeURIComponent(jobId)}/review`);
  return res.review ?? null;
}

export interface SubmitConfirmedArgs {
  subject: Record<string, unknown>;
  comps: Record<string, unknown>[];
  tier?: "cma_calc" | "cma_ai" | "amb";
  meta?: Record<string, unknown>;
  images?: string[];
  /** The `/api/amb/extract` job this confirm payload was reviewed from — required for any
   * server-verified photo status to survive (see amb_report.py's `_reconcile_client_photo_status`). */
  sourceJobId?: string;
}

function withReaderZone(meta: Record<string, unknown>): Record<string, unknown> {
  if (typeof meta.tz === "string" && meta.tz) return meta;
  const tz = readerZone();
  return tz ? { ...meta, tz } : meta;
}

export async function submitConfirmed(args: SubmitConfirmedArgs): Promise<{ job: AmbJobPublic; notice: string }> {
  return apiFetch<{ job: AmbJobPublic; notice: string }>("/api/amb/jobs", {
    method: "POST",
    body: {
      subject: args.subject,
      comps: args.comps,
      tier: args.tier ?? "amb",
      // The reader's zone is added unless the caller already set one (S09).
      meta: withReaderZone(args.meta ?? {}),
      images: args.images ?? [],
      source_job_id: args.sourceJobId ?? null,
    },
  });
}

/** One layout the server offers. Mirrors `engine.formats.Layout.as_dict()`. */
export type LayoutOption = {
  code: string;        // FCS | FOL | PAN | ALB
  name: string;        // Focus | Folio | Panorama | Album
  describe: string;    // what the document is FOR — never an extent
  landscape: boolean;
  docx: boolean;       // false when that layout cannot be produced as a Word file
};

/**
 * The layout list, ALWAYS from the server.
 *
 * Deliberately not a constant in this file. `engine/formats.py` is the registry, and a second
 * copy living in the client is drift waiting to happen — the same rule `/api/branding` follows,
 * which `test_branding_next_contract.py` exists to enforce. A layout whose renderer has not
 * landed, or which cannot make a Word file, is described correctly by the server or not at all.
 */
export async function getFormats(): Promise<{ formats: LayoutOption[]; legacy: string }> {
  return apiFetch<{ formats: LayoutOption[]; legacy: string }>("/api/amb/formats");
}

/**
 * `fmt` is the FILE (pdf | docx). `layout` is the DESIGN (FCS | FOL | PAN | ALB).
 *
 * Omitting `layout` is not a default — the server hands a caller that names none the brief it has
 * always received. Switching layout re-renders from the frozen payload and never calls the model,
 * so it is free to change as often as the agent likes.
 */
/**
 * Remember which layout this brief is in, so leaving the screen does not lose the choice.
 *
 * FIRE AND FORGET IS NOT GOOD ENOUGH HERE and the caller is expected to await it: the download is
 * a plain `<a href>`, so a click that lands before the choice is recorded downloads the old
 * layout. The screen keeps its own state either way — this is what makes the choice
 * survive a reload, not what makes the current page work.
 */
export async function setJobLayout(jobId: string, layout: string): Promise<{ layout: string }> {
  return apiFetch<{ layout: string }>(
    `/api/amb/jobs/${encodeURIComponent(jobId)}/layout`,
    { method: "POST", body: { layout } },
  );
}

export function downloadUrl(
  jobId: string,
  fmt: "pdf" | "docx" = "pdf",
  layout?: string,
): string {
  const base = `/api/amb/jobs/${encodeURIComponent(jobId)}/download?fmt=${fmt}`;
  return layout ? `${base}&layout=${encodeURIComponent(layout)}` : base;
}

// ---------------------------------------------------------------------------
// Subject lookup — county CAD (authoritative for protest; a fill source for CMA/AMB)
// ---------------------------------------------------------------------------

export interface SubjectLookupResult {
  subject: Record<string, unknown> | null;
  guidance?: string;
  /**
   * One of "ok" / "no_dataset" / "no_match" / "ambiguous" / "no_query" / "error"
   * (`ingestion/registry.py`'s SUBJECT_* vocabulary). "ambiguous" means the county record
   * matched MORE THAN ONE property for this address — a different fact than "no_match" (we
   * looked and nothing fit), and callers must not collapse the two into one "holds no
   * property" message.
   */
  status?: string;
  could_not_ask?: boolean;
}

export async function lookupCountySubject(state: string, county: string, q: string): Promise<SubjectLookupResult> {
  const params = new URLSearchParams({ state, county, q });
  return apiFetch<SubjectLookupResult>(`/api/subject?${params.toString()}`);
}

/**
 * `GET /api/next/cad/available` — is there county CAD data for THIS address?
 *
 * THE ONE PREDICATE (slice S05). The county the address is actually in, derived from its ZIP by
 * `server/cad_availability.py`, and NOT `counties[0]`, which is what the CAD fallback used to gate
 * on. Makes no provider call and spends no allowance; `resolve` is off by default so it reads no
 * property row either.
 */
export interface CadAvailability {
  available: boolean;
  state: string | null;
  county: string | null;
  zip: string | null;
  reason_code: string;
  reason: string;
  /** True when the county record was never CONSULTED — never read as evidence about the property. */
  could_not_ask: boolean;
}

export async function cadAvailableForAddress(address: string): Promise<CadAvailability> {
  const params = new URLSearchParams({ address });
  return apiFetch<CadAvailability>(`/api/next/cad/available?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// County CAD type-ahead — the subject picker (slice data-cad-picker, 2026-10-04)
//
// OWNER DESIGN: the type-ahead is CAD ONLY. No Cotality call is made while the agent types. Each
// row is one county record, and picking it makes that record the subject, locked by `prop_id`
// exactly as a picked Cotality suggestion locks a clip.
//
// THE CONTRACT IS TS's ROUTE, `src/server/cad_picker.py` (`POST /api/cad/typeahead {input}`): the
// same request and envelope as `/api/cotality/typeahead` (`{results, source_tag, match_count}`),
// plus `reason_code` / `reason` / `could_not_ask`, because "we hold that county and the address
// isn't in it" and "we hold no dataset for that ZIP" are different facts and the screen must not
// collapse them. The route is guarded by the monthly limit and answers 429 `limit_reached` there.
//
// Every per-row field is optional here and normalized in ONE place (`./cadPick.ts`): a row with no
// `prop_id` or no address is dropped, never rendered as a blank, unpickable line, and nothing
// missing is ever defaulted to zero.
// ---------------------------------------------------------------------------

/** The route. One constant, so the client and its tests can never disagree about it. */
export const CAD_TYPEAHEAD_PATH = "/api/cad/typeahead";

/** `cad_picker.suggestion_from_row`'s `lock` — sent back as `meta.cad_pick` so the confirm route
 * can re-read the same record by prop_id and keep its source tags. */
export interface CadLock {
  state?: string | null;
  county?: string | null;
  prop_id?: string | null;
  dataset_version?: string | null;
}

export interface LivingAreaFlagged {
  value: number;
  flag: string;
  reason: string;
}

export interface CadSuggestion {
  prop_id?: string | number;
  /** The full situs line (`situs_display`). */
  address?: string | null;
  city?: string | null;
  zip?: string | null;
  /** The labelled property type, or null when the CAD's code is not one the server can label. */
  property_type?: string | null;
  /** The CAD's own code — shown raw when there is no label, never guessed. */
  property_type_code?: string | null;
  living_area_sqft?: number | null;
  /** Set when the county states a living area under 100 sq ft: the value is kept here, flagged, and
   * `living_area_sqft` is null so it never fills GLA or reaches comp math. */
  living_area_flagged?: LivingAreaFlagged | null;
  /** The ZIP+4 add-on ("2731"), when the county states one. */
  zip4?: string | null;
  year_built?: number | null;
  state?: string | null;
  county?: string | null;
  /** The short name, e.g. "Collin CAD" — what every source tag starts with. */
  cad?: string | null;
  /** The district's full name from the baked manifest, e.g. "Collin Central Appraisal District". */
  cad_label?: string | null;
  dataset_version?: string | null;
  record_id?: string | null;
  /** "Collin CAD, record 123, dataset v" — the record-level tag. */
  source_tag?: string | null;
  /** The record in the subject grid's own vocabulary (address, gla, year, beds). */
  subject?: Record<string, unknown>;
  /** Per-field provenance tag, keyed like `subject`. */
  field_sources?: Record<string, string>;
  /** Why a row the county stated something about is still blank, keyed by grid key (2026-10-18):
   * e.g. a Dallas lot whose unit DCAD does not state. Shown in that row instead of
   * "Nothing on record". Absent on an older server. */
  field_notes?: Record<string, string>;
  /** The state of a FILLED field that is not a plain reading of the record, keyed by grid key
   * (2026-10-19): a Dallas lot whose unstated unit frontage x depth confirmed is
   * `{state: "inferred unit", note: "Unit not stated by DCAD; confirmed as square feet by …"}`,
   * and its acreage `{state: "computed"}`. Shown beside the source tag. Absent on an older server. */
  field_states?: Record<string, { state: string; note?: string }>;
  lock?: CadLock;
  /** How close the typed text came: "exact" | "normalized" | "space_insensitive" | "variant".
   * The address above is the county's own spelling whichever matched. */
  match?: string | null;
  [k: string]: unknown;
}

export interface CadTypeaheadResult {
  results: CadSuggestion[];
  source_tag?: string;
  match_count?: number;
  /** Set on an EMPTY answer only: "not_in_dataset" / "no_dataset_for_zip" / "no_zip" /
   * "unreadable" / "too_short". Branched on; the prose in `reason` is printed. */
  reason_code?: string | null;
  reason?: string | null;
  could_not_ask?: boolean;
  /** On an EMPTY answer: the county's own street spellings nearest to what was typed, at the
   * typed house number (owner spec 2026-10-04 §4). Following one searches again; it never picks. */
  did_you_mean?: CadDidYouMean[];
}

export interface CadDidYouMean {
  /** The street exactly as the county spells it, e.g. "SPRING TREE LN". */
  street?: string;
  /** What choosing it puts in the address field, e.g. "4815 SPRING TREE LN". */
  query?: string;
  distance?: number;
  county?: string;
  state?: string;
}

export async function cadTypeahead(input: string, signal?: AbortSignal): Promise<CadTypeaheadResult> {
  return apiFetch<CadTypeaheadResult>(CAD_TYPEAHEAD_PATH, { method: "POST", body: { input }, signal });
}

// ---------------------------------------------------------------------------
// Field history + the protest value choice (owner spec 2026-10-04 §2, §LVS `src/server/
// field_history.py` — landed on this worktree 2026-10-04, read directly rather than re-briefed).
//
// `GET /api/cad/field-history?county=&prop_id=&field=`: every value the shipped county file holds
// for one field, newest first, each already carrying its own finished label — the server composes
// "<Label> <display>: <tag>" (`ingestion.core.provenance.value_label`) once, and this client
// prints it verbatim (ADR-0002). A stage/year the file does not hold is in `not_in_dataset` with
// the server's own reason, never invented or inferred from a date.
//
// `POST /api/cad/value-choice {county, prop_id, field, stage, year, dataset_version}`: re-reads
// the history and returns the chosen value as a stored-able record. `POST /api/analyses` accepts
// the SAME shape as `AnalyzeIn.value_choice` and resolves it again server-side — a pick is never
// taken on the client's word.
// ---------------------------------------------------------------------------

export const CAD_FIELD_HISTORY_PATH = "/api/cad/field-history";
export const CAD_VALUE_CHOICE_PATH = "/api/cad/value-choice";

/** Where one value came from in the shipped file — display only; never a key a client builds a
 * request from. */
export interface CadFieldHistoryRecord {
  prop_id: string;
  file?: string | null;
  record_id?: string | null;
  column?: string | null;
}

/** One real value on the field's timeline, exactly as `field_history.build_history` sends it.
 * `label` is the whole finished sentence — render it, do not recompose one from the parts. */
export interface CadFieldHistoryValue {
  value: string | number | boolean | null;
  display: string;
  stage: string | null;
  year: number | null;
  /** True on the single newest/current entry — the one this dataset's live roll states. */
  current: boolean;
  date: string | null;
  date_kind?: "export_dated" | "loaded" | null;
  record: CadFieldHistoryRecord;
  basis?: string | null;
  source_tag: string;
  label: string;
}

/** A stage (optionally a stage+year) the shipped file does not hold for this field — reported,
 * never filled in. `reason` is the server's own words, e.g. "not in this dataset". */
export interface CadFieldHistoryMissing {
  stage?: string | null;
  year?: number | null;
  reason: string;
}

export interface CadFieldHistoryResult {
  county?: string;
  cad?: string;
  prop_id?: string;
  field?: string;
  field_label?: string;
  dataset_version?: string | null;
  /** NEWEST FIRST, exactly as the server orders it. This client never re-sorts — the ordering is
   * itself part of what "newest value, clearly labelled" promises. */
  values?: CadFieldHistoryValue[];
  /** `values[0]`, or null when the file holds nothing for this field — the default selection for
   * a protest value chooser, per the owner's "default = latest". */
  default?: CadFieldHistoryValue | null;
  not_in_dataset?: CadFieldHistoryMissing[];
  /** Present only for land area / acreage on a lot whose county record states a lot size and an
   * acreage that disagree (`ingestion.core.landcheck`, 2026-10-17). Then `values` is empty and
   * `default` is null: no single number is served. `note` is the calm sentence to print, `figures`
   * are both figures, each with its own finished `label` (figure + source tag) — render verbatim. */
  land_area_conflict?: CadLandAreaConflict | null;
}

export interface CadLandAreaFigure {
  field: "land_area_sqft" | "land_acreage";
  value: number;
  display: string;
  column: string;
  source_tag: string;
  label: string;
}

export interface CadLandAreaConflict {
  note: string;
  detail: string;
  figures: CadLandAreaFigure[];
}

export async function cadFieldHistory(
  county: string,
  propId: string,
  field: string,
  signal?: AbortSignal,
): Promise<CadFieldHistoryResult> {
  const params = new URLSearchParams({ county, prop_id: propId, field });
  return apiFetch<CadFieldHistoryResult>(`${CAD_FIELD_HISTORY_PATH}?${params.toString()}`, { signal });
}

/** The pick a protest value chooser sends — the exact shape `field_history.resolve_value_choice`
 * and `AnalyzeIn.value_choice` both read. `dataset_version` pins the pick to the file it was made
 * against; sending the version the history answer carried is how a stale pick is refused rather
 * than silently resolved against a newer file. */
export interface CadValueChoiceRequest {
  county: string;
  prop_id: string;
  field: string;
  stage: string;
  year: number;
  dataset_version?: string | null;
  state?: string;
}

/** The re-read, stored-able pick `POST /api/cad/value-choice` and `create_analysis` both return. */
export interface CadValueChoice extends CadFieldHistoryValue {
  county: string;
  cad: string;
  prop_id: string;
  field: string;
  field_label: string;
  dataset_version: string | null;
  chosen_at: string;
  chosen_by?: string;
}

export async function cadValueChoice(
  body: CadValueChoiceRequest,
  signal?: AbortSignal,
): Promise<CadValueChoice> {
  return apiFetch<CadValueChoice>(CAD_VALUE_CHOICE_PATH, { method: "POST", body, signal });
}

/** The protest-value choice, AS STATED AGAINST WHAT THE ANALYSIS ACTUALLY COMPUTED ON
 * (`field_history.choice_against_reco`) — the shape `AnalysisResult.valueChoice` carries. */
export interface AnalysisValueChoice extends CadValueChoice {
  /** True only when the analysis's own subject figure IS this exact record/dataset/value. */
  relied_on: boolean;
  /** The sentence the brief prints — "This brief relies on: ..." or the chosen-but-not-used
   * caveat. Render verbatim; never recompose it from `relied_on`. */
  statement: string;
}

/** The minimal request `POST /api/analyses` (`AnalyzeIn`) needs to run a protest analysis with a
 * value choice attached. Only the fields this client actually sends are typed; the server accepts
 * more (`vertical`, etc.) that no caller here sets. */
export interface AnalyzeArgs {
  state: string;
  county: string;
  vertical?: string;
  prop_id?: string | null;
  address?: string | null;
  value_choice?: CadValueChoiceRequest | null;
}

export interface AnalysisResult {
  valueChoice?: AnalysisValueChoice;
  /** An empty-state guidance answer rather than a saved analysis — `reco.get("ok", True)` false
   * on the server. Anything else on this object is the engine's recommendation payload, which no
   * caller in this module reads yet. */
  guidance?: unknown;
  [k: string]: unknown;
}

export async function createAnalysis(body: AnalyzeArgs, signal?: AbortSignal): Promise<AnalysisResult> {
  return apiFetch<AnalysisResult>("/api/analyses", { method: "POST", body, signal });
}

// ---------------------------------------------------------------------------
// Cotality — national fill (inert unless VALUBRIEF_COTALITY_ENABLED=1; see server/cotality.py).
// Every result here is UNCHECKED and source-tagged; nothing here is auto-selected into a report.
// ---------------------------------------------------------------------------

export interface CotalityTypeaheadResult {
  [k: string]: unknown;
}

export async function cotalityTypeahead(input: string): Promise<CotalityTypeaheadResult> {
  return apiFetch<CotalityTypeaheadResult>("/api/cotality/typeahead", { method: "POST", body: { input } });
}

export interface CotalityLookupResult {
  data: Record<string, unknown>;
  source_tag: string;
  confirmed: boolean;
  _note?: string;
  /** THE RESOLVED CLIP, which the route has always returned and this type never declared
   * (`cotality._lookup_subject` ends `return {..., "clip": clip}`). Declaring it is what lets a
   * caller that looked a subject up BY ADDRESS keep the identifier the provider resolved, instead
   * of making the provider resolve it again on the next call — measured at 3 outbound calls and
   * one cap unit per report in `tests/test_cotality_report_call_census.py`. Optional because a
   * lookup that found nothing has no clip to give. */
  clip?: string;
}

export async function cotalityLookupSubject(args: { address?: string; clip?: string }): Promise<CotalityLookupResult> {
  return apiFetch<CotalityLookupResult>("/api/cotality/lookup", { method: "POST", body: args });
}

export async function cotalityComps(args: { address?: string; clip?: string }): Promise<CotalityLookupResult> {
  return apiFetch<CotalityLookupResult>("/api/cotality/comps", { method: "POST", body: args });
}

// ---------------------------------------------------------------------------
// The source cascade (2026-08-11 — server/source_cascade.py, registered in app.py as
// `/api/next/cascade/*`). Two REAL, already-implemented and tested routes; nothing is invented
// here either. Read `docs/product/SOURCE-CASCADE-SPEC.md` before changing anything that consumes
// them, and `client/src/cma/cascade.ts` for the payload types and the rules for reading it.
//
// The two are deliberately separate calls. `/status` answers "what should this screen SAY before
// the agent has typed anything" — the sandbox warning, the licence stamp, the comp floor — and it
// makes NO external call and spends NONE of the 25-a-day Cotality allowance. `/resolve` is the one
// that costs something, and it is only ever called once the agent has given us an address.
// ---------------------------------------------------------------------------

/** Gates and rung availability. Safe to call on mount: no provider call, no cap spend. */
export async function getCascadeStatus(): Promise<CascadeStatus> {
  return apiFetch<CascadeStatus>("/api/next/cascade/status");
}

/**
 * Run both ladders once. The ONLY required input is an address (or a clip already locked from the
 * type-ahead); everything else is optional and omitting all of it is the ordinary path — that is
 * the spec's "soft pass" and it produces no warning.
 *
 * The `subject` and `comps` that come back are in the engine shape `POST /api/amb/jobs` already
 * takes, so they flow into the existing review grid and the existing submit with no transformation.
 */
export async function resolveCascade(args: CascadeResolveArgs): Promise<CascadeResolveResult> {
  return apiFetch<CascadeResolveResult>("/api/next/cascade/resolve", { method: "POST", body: args });
}

// ---------------------------------------------------------------------------
// /next additive endpoints (Session 7 — server/amb_next.py). Neither existed before this
// session; both are new, small, additive files, not part of the pre-existing contract.
// ---------------------------------------------------------------------------

/** The REAL, currently-loaded (state, county) datasets — grounds the readiness gate's county
 * picker in what this deployment actually has, never an aspirational statewide list. */
export async function getLoadedCounties(): Promise<LoadedCounty[]> {
  const res = await apiFetch<{ counties: LoadedCounty[] }>("/api/next/counties/loaded");
  return res.counties;
}

/** Attach or replace the CONFIRMED subject photo on a finished brief (2026-09-25). The server
 * re-renders every download from the stored brief, so nothing is recomputed and no figure moves;
 * the photo passes the same gate as an intake upload and is labeled as the agent's own. */
export async function setSubjectPhoto(jobId: string, photo: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/api/amb/jobs/${encodeURIComponent(jobId)}/subject-photo`, {
    method: "POST",
    body: { photo },
  });
}

/** The finished report's full data object (same one the PDF/DOCX renderer consumes) — lets the
 * client render the narrative inline instead of only offering a file download. */
export async function getJobResult(jobId: string): Promise<AmbReportResult> {
  return apiFetch<AmbReportResult>(`/api/next/amb/jobs/${encodeURIComponent(jobId)}/result`);
}

// ---------------------------------------------------------------------------
// Editing a finished brief's PROSE (2026-08-01 — server/amb_edit.py). Nothing here can reach a
// number: the server's allow-list refuses every computed field, and the patched report is put back
// through the same honesty gate the generated one had to clear. See that module's docstring.
//
// The plain-text conversion is deliberately NOT done here. `getEditableReport` returns the stored
// HTML fragments already stripped and unescaped by the server, and `patchReport` sends plain text
// back — one implementation of that round trip, so the agent's own words cannot be mangled by two
// strippers that disagree about an entity.
// ---------------------------------------------------------------------------

export interface EditableReportFields {
  lead: string;
  about: string;
  sitting: string;
  how_reached: string;
  pricing_rationale: string;
  neighborhood: string[];
  note: string[];
  card_blurbs: Record<string, string>;
}

export interface EditableReport {
  id: string;
  fields: EditableReportFields;
  /** Each comparable card's name, so an edit box can be labelled with the sale it belongs to
   * rather than its index. */
  card_names: string[];
  edited_fields: string[];
  edited_at: string | null;
}

export async function getEditableReport(jobId: string): Promise<EditableReport> {
  return apiFetch<EditableReport>(`/api/amb/jobs/${encodeURIComponent(jobId)}/report/editable`);
}

/** A PATCH names only what changed. An omitted key is untouched server-side. */
export type ReportEditPatch = Partial<EditableReportFields>;

export async function patchReport(
  jobId: string,
  patch: ReportEditPatch
): Promise<{ ok: boolean; changed: string[] }> {
  return apiFetch<{ ok: boolean; changed: string[] }>(
    `/api/amb/jobs/${encodeURIComponent(jobId)}/report`,
    { method: "PATCH", body: patch }
  );
}

// ---------------------------------------------------------------------------
// Street View (Session 8 — server/amb_next.py). Wraps the already-shipped, previously-uncalled
// `providers/streetview.py` scaffold. Display-only reference photo, never part of a PDF/DOCX
// deliverable while `deliverable_license` is 0 (strict-tier — legality wins; see that module).
// ---------------------------------------------------------------------------

export interface StreetViewStatus {
  enabled: boolean;
  cacheable: boolean;
  deliverable_license: boolean;
  attribution: string;
  gcp_key_configured: boolean;
  base_url: string;
  billing_note: string;
}

export async function getStreetViewStatus(): Promise<StreetViewStatus> {
  return apiFetch<StreetViewStatus>("/api/next/streetview/status");
}

/** Not fetched with `apiFetch` (that helper expects a JSON body) — this is an `<img src>` URL. The
 * browser sends the same-origin `vb_session` cookie automatically, so no separate auth wiring is
 * needed for the `<img>` tag itself; the endpoint still 401s a logged-out request the same as any
 * other `/next` route. */
export function streetViewImageUrl(address: string): string {
  const params = new URLSearchParams({ address });
  return `/api/next/streetview/image?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Location map (2026-07-31 — server/amb_next.py). TWO separate endpoints on purpose: `/status`
// answers "should anything render at all" without spending a key fetch, and `/browser-key` returns
// the HTTP-referrer-restricted Maps JavaScript key. That key is a DIFFERENT credential from the
// server-side one (`providers/maps.py::browser_key` refuses to fall back to it) because a browser
// key is public by construction — see LocationMap.tsx.
// ---------------------------------------------------------------------------

export interface MapStatus {
  enabled: boolean;
  cacheable: boolean;
  deliverable_license: boolean;
  attribution: string;
  server_key_configured: boolean;
  browser_key_configured: boolean;
  max_labelled_markers: number;
}

export interface MapBrowserKey {
  key: string;
  attribution: string;
  max_labelled_markers: number;
}

/** 1G — the comp-search panel's defaults and ranges (server-read; no source is asked). */
export async function getCompSearchDefaults(): Promise<import("./compCriteria").CompSearchDefaults> {
  return apiFetch("/api/next/comp-search/defaults");
}

/** 1G — which of the comps the agent already holds fall inside the criteria. Pure; nothing is spent. */
export async function evaluateCompCriteria(body: {
  comp_criteria: Record<string, unknown>;
  subject: Record<string, unknown>;
  rows: unknown[];
}): Promise<{ verdicts: import("./compCriteria").RowVerdict[]; skipped: string[] }> {
  return apiFetch("/api/next/comp-search/evaluate", { method: "POST", body });
}

/** C1 — the engine's own "Best match" order of the rows the agent holds, as indices into `rows`,
 * and the first three that can price a report. Pure; nothing is spent, no figure is read back. */
export interface BestMatchRank {
  order: number[];
  recommended: number[];
  target: number;
}
export async function rankComps(body: {
  subject: Record<string, unknown>;
  rows: unknown[];
}): Promise<BestMatchRank> {
  return apiFetch("/api/next/comp-search/rank", { method: "POST", body });
}

export async function getMapStatus(): Promise<MapStatus> {
  return apiFetch<MapStatus>("/api/next/map/status");
}

export async function getMapBrowserKey(): Promise<MapBrowserKey> {
  return apiFetch<MapBrowserKey>("/api/next/map/browser-key");
}

// ---------------------------------------------------------------------------
// Capture tokens — the credential the browser extension authenticates with (`X-ValuBrief-Token`).
//
// These three endpoints are PRE-EXISTING (`server/projects.py`: POST/GET `/api/capture-token`,
// DELETE `/api/capture-token/{id_or_prefix}`), session-authed, and shipped long before `/next`
// existed. Nothing new is invented here — until now the only UI for them lived in Apex's
// `index.html`, which is why an agent on `/next` had no way to connect the extension at all.
//
// The raw token is returned by the CREATE call and never again: only its SHA-256 hash is stored,
// so the server genuinely cannot show it a second time. Any screen built on this has to treat that
// as a one-shot, not as a value it can re-read later.
// ---------------------------------------------------------------------------

export interface CaptureTokenPublic {
  /** A short prefix of the token's HASH — safe to display, useless as a credential. */
  id_prefix: string;
  label: string | null;
  created_at: string | null;
}

export async function listCaptureTokens(): Promise<CaptureTokenPublic[]> {
  const res = await apiFetch<{ tokens: CaptureTokenPublic[] }>("/api/capture-token");
  return res.tokens ?? [];
}

/** Returns the RAW token exactly once. There is no second chance to read it. */
export async function createCaptureToken(label?: string): Promise<{ token: string } & CaptureTokenPublic> {
  return apiFetch<{ token: string } & CaptureTokenPublic>("/api/capture-token", {
    method: "POST",
    body: { label: label ?? "Browser extension" },
  });
}

export async function revokeCaptureToken(idPrefix: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/api/capture-token/${encodeURIComponent(idPrefix)}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Session identity — for the app bar's account cluster (UI-001). Both endpoints are pre-existing
// (`server/app.py`: `GET /api/me` at :695, `POST /api/auth/logout` at :678); nothing new is
// invented here. `getMe` is deliberately allowed to fail quietly: the bar renders without an email
// rather than blocking the app on an identity call.
// ---------------------------------------------------------------------------

export interface MeResult {
  user?: { email?: string; [k: string]: unknown } | null;
}

export async function getMe(): Promise<MeResult> {
  // No redirect-on-401 here: an anonymous visitor asking "who am I" is a legitimate answer of
  // "nobody", not a session expiry to bounce on. The route guard owns that decision.
  return apiFetch<MeResult>("/api/me", { onUnauthorized: () => {} });
}

export async function logout(): Promise<void> {
  await apiFetch<{ ok: boolean }>("/api/auth/logout", { method: "POST", onUnauthorized: () => {} });
}
